import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  deleteOne as deleteBrowseHomePage,
  fetchAll as fetchBrowseHomePages,
  saveMetadata
} from 'app/api/admin/browse/homePageApi';
import ErrorPage from 'app/components/ErrorPage';
import { MESSAGE_ACTION_GENERIC_ERROR, MESSAGE_ACTION_GENERIC_SUCCESS } from 'app/constants/admin/browse';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { LOCALE_CODE } from 'app/constants/language';
import { hasAdminPermissions, makeHomeListPageRoute } from 'app/helpers/admin/browse/home';
import useApiClient from 'app/hooks/useApiClient';
import useLocaleCodes from 'app/hooks/useLocalCodes';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import AdminBrowseHomeListPage from './AdminBrowseHomeListPage';

const AdminBrowseHomeListPageContainer = ({ localeCode }) => {
  const apiClient = useApiClient();

  const dispatch = useDispatch();
  const history = useHistory();

  const { availableLocaleCodes, isAvailableLocaleCode, mapLocaleCodeToLabel } = useLocaleCodes();

  const currentLibrary = useSelector(selectCurrentLibrary);
  const subdomain = currentLibrary.get('domain');

  const currentUser = useSelector(selectCurrentUser);

  const [state, setState] = useState({
    browseHomePages: [],
    browseHomePagesMetadata: {},
    isFetching: true,
    isSaving: false
  });

  const { browseHomePages, browseHomePagesMetadata, isFetching, isSaving } = state;
  const { activeBrowseHomePageId } = browseHomePagesMetadata;

  const localeDropdownOptions = Immutable.List(availableLocaleCodes).map(availableLocaleCode => {
    return Immutable.Map({
      disabled: false,
      label: mapLocaleCodeToLabel(availableLocaleCode),
      selected: availableLocaleCode === localeCode,
      value: availableLocaleCode
    });
  });

  useEffect(() => {
    setState(prevState => {
      return {
        ...prevState,
        browseHomePages: [],
        isFetching: true
      };
    });

    fetchBrowseHomePages(apiClient, localeCode)
      .then(({ browseHomePages: fetchedBrowseHomePages, metadata }) => {
        setState(prevState => {
          return {
            ...prevState,
            browseHomePages: fetchedBrowseHomePages,
            browseHomePagesMetadata: metadata
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isFetching: false
          };
        });
      });
  }, [apiClient, dispatch, localeCode, setState, subdomain]);

  const handleActivateBrowseHomePage = newActiveBrowseHomePageId => {
    const newBrowseHomePagesMetadata = {
      activeBrowseHomePageId: newActiveBrowseHomePageId,
      localeCode,
      subdomain
    };

    setState(prevState => {
      return {
        ...prevState,
        isSaving: true
      };
    });

    saveMetadata(apiClient, newBrowseHomePagesMetadata)
      .then(savedBrowseHomePagesMetadata => {
        dispatch(MESSAGE_ACTION_GENERIC_SUCCESS);

        setState(prevState => {
          return {
            ...prevState,
            browseHomePagesMetadata: savedBrowseHomePagesMetadata
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isSaving: false
          };
        });
      });
  };

  const handleDeleteBrowseHomePage = broseHomePageId => {
    setState(prevState => {
      return {
        ...prevState,
        isSaving: true
      };
    });

    deleteBrowseHomePage(apiClient, localeCode, broseHomePageId)
      .then(() => {
        dispatch(MESSAGE_ACTION_GENERIC_SUCCESS);

        const newBrowseHomePages = browseHomePages.filter(browseHomePage => {
          return browseHomePage.browseHomePageId !== broseHomePageId;
        });

        setState(prevState => {
          return {
            ...prevState,
            browseHomePages: newBrowseHomePages
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isSaving: false
          };
        });
      });
  };

  const handleLocaleCodeChange = newLocaleCode => {
    history.push(makeHomeListPageRoute(newLocaleCode));
  };

  const canViewPage = hasAdminPermissions(currentUser);
  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  if (!isAvailableLocaleCode(localeCode)) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  return (
    <AdminBrowseHomeListPage
      activeBrowseHomePageId={activeBrowseHomePageId}
      browseHomePages={browseHomePages}
      isFetching={isFetching}
      isSaving={isSaving}
      localeCode={localeCode}
      localeDropdownOptions={localeDropdownOptions}
      mapLocaleCodeToLabel={mapLocaleCodeToLabel}
      onActivate={handleActivateBrowseHomePage}
      onDelete={handleDeleteBrowseHomePage}
      onLocaleCodeChange={handleLocaleCodeChange}
    />
  );
};

AdminBrowseHomeListPageContainer.propTypes = {
  localeCode: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired
};

export default AdminBrowseHomeListPageContainer;
